'use client';

import { defineMessages, useIntl } from 'react-intl';

import { useFormatDiscount } from '@packages/shared/src/hooks/useFormatDiscount/useFormatDiscount';
import { useFormatPrice } from '@packages/shared/src/hooks/useFormatPrice/useFormatPrice';

import { Price } from '@packages/shared/src/ui/Price/Price';
import { PriceDiscount } from '@packages/shared/src/ui/Price/PriceDiscount/PriceDiscount';
import { PriceUnitPrice } from '@packages/shared/src/ui/Price/PriceUnitPrice/PriceUnitPrice';
import { PriceValue } from '@packages/shared/src/ui/Price/PriceValue/PriceValue';

export const priceMessages = defineMessages({
  msrp: {
    id: 'price.label.msrp',
    defaultMessage: 'UVP',
  },
  oldPrice: {
    id: 'price.label.oldPrice',
    defaultMessage: 'statt',
  },
  startPricePrefix: { id: 'price.start.from', defaultMessage: 'ab' },
});

type ProductCardV2PriceProps = {
  id: string;
  price: {
    currentPrice: number;
    currency: string;
    isStartPrice?: boolean | null;
    discount?:
      | ({
          previousPrice: number;
          wasMsrp?: boolean | null;
        } & ({ discountPercentage: number } | { priceDifference: number }))
      | null;
    unitPackage?: {
      price: number;
      amount: number;
      unit: string;
    } | null;
  };
  fixedValueTextClass?: string;
};

export const ProductCardV2Price = ({ id, price, fixedValueTextClass }: ProductCardV2PriceProps) => {
  const { formatPrice } = useFormatPrice();
  const { formatDiscount } = useFormatDiscount();
  const { formatMessage } = useIntl();

  return (
    <Price
      id={`product-${id}-price`}
      className="product-card-price mx-2 mt-1 grid-rows-subgrid [grid-area:4/2/span_5/span_1]"
    >
      <PriceValue fixedTextClass={fixedValueTextClass}>
        {price.isStartPrice ? `${formatMessage(priceMessages.startPricePrefix)} ` : ''}
        {formatPrice(price.currentPrice, price.currency)}
      </PriceValue>
      {price.discount && (
        <PriceDiscount
          oldPrice={`${price.discount.wasMsrp ? formatMessage(priceMessages.msrp) : formatMessage(priceMessages.oldPrice)} ${formatPrice(price.discount.previousPrice, price.currency)}`}
          discount={`- ${formatDiscount({ currency: price.currency, ...price.discount })}`}
        />
      )}
      {price.unitPackage && (
        <PriceUnitPrice>{`${formatPrice(price.unitPackage?.price, price.currency)} / ${price.unitPackage.amount ?? ''}${price.unitPackage.unit}`}</PriceUnitPrice>
      )}
    </Price>
  );
};
